import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, X, Send, Loader2, CalendarCheck } from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";
import { clarityEvent } from "@/lib/clarity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: "", phone: "", email: "", service: "", message: "" };

export const BookingWidget = () => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  // Show widget after user has scrolled past the hero (~500px)
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Fyll i namn och telefon.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        message: form.message || "Snabbförfrågan via boka-besiktning-widget.",
      });
      toast.success("Tack! Vi ringer dig så snart som möjligt.");
      clarityEvent("booking_form_submit");
      setForm(emptyForm);
      setOpen(false);
    } catch (err) {
      toast.error("Något gick fel. Ring oss direkt så hjälper vi dig!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button (desktop) */}
      <button
        data-testid="booking-widget-toggle"
        onClick={() => setOpen(true)}
        aria-label="Boka gratis besiktning"
        className={`hidden md:inline-flex fixed z-40 bottom-6 right-6 group items-center gap-2 bg-[#D92525] hover:bg-white text-white hover:text-[#0B132B] font-display font-bold text-sm tracking-[0.15em] uppercase px-5 py-4 shadow-2xl border-2 border-[#D92525] hover:border-[#0B132B] transition-all duration-300 ${
          visible && !open
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-6 pointer-events-none"
        }`}
      >
        <CalendarCheck size={18} strokeWidth={2.4} className="shrink-0" />
        <span>Boka besiktning</span>
        {/* Pulse ring */}
        <span className="absolute inset-0 -z-10 animate-ping-slow bg-[#D92525]/40" aria-hidden />
      </button>

      {/* Mobile bottom action bar */}
      {!open && (
        <div
          data-testid="mobile-action-bar"
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 grid grid-cols-2 border-t-2 border-[#D92525] bg-[#0B132B] shadow-[0_-6px_20px_rgba(0,0,0,0.35)]"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <a
            href={SITE.phoneHref}
            data-testid="mobile-bar-call"
            className="inline-flex items-center justify-center gap-2 h-14 font-display font-bold text-xs tracking-[0.15em] uppercase text-white border-r border-white/15 active:bg-white/10"
          >
            <Phone size={16} strokeWidth={2.4} /> Ring oss
          </a>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="mobile-bar-book"
            className="inline-flex items-center justify-center gap-2 h-14 font-display font-bold text-xs tracking-[0.15em] uppercase text-white bg-[#D92525] active:bg-[#b81e1e]"
          >
            <CalendarCheck size={16} strokeWidth={2.4} /> Boka besiktning
          </button>
        </div>
      )}

      {/* Modal overlay */}
      {open && (
        <div
          data-testid="booking-widget-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-5"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="relative w-full sm:max-w-md bg-white text-[#0B132B] shadow-2xl border-2 border-[#0B132B] max-h-[92vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-[#0B132B] text-white px-6 py-5 flex items-start justify-between">
              <div>
                <div className="inline-flex items-center gap-2 font-display font-bold text-[10px] uppercase tracking-[0.2em] text-[#FFB3B3]">
                  <span className="w-6 h-[2px] bg-[#FFB3B3]" /> Snabbförfrågan
                </div>
                <h3 className="font-display font-black text-xl md:text-2xl mt-1.5 tracking-tight">
                  Boka gratis besiktning
                </h3>
                <p className="font-body text-xs text-white/70 mt-1">
                  Vi ringer dig inom 24h
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Stäng"
                data-testid="booking-widget-close"
                className="p-2 border-2 border-white/40 hover:bg-white hover:text-[#0B132B] transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={submit} data-testid="booking-widget-form" className="p-5 md:p-6 space-y-4">
              <div>
                <label className="block font-display font-bold text-[11px] uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  Namn *
                </label>
                <input
                  data-testid="booking-widget-name"
                  type="text"
                  required
                  placeholder="För- och efternamn"
                  value={form.name}
                  onChange={update("name")}
                  className="swed-input"
                />
              </div>

              <div>
                <label className="block font-display font-bold text-[11px] uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  Telefon *
                </label>
                <input
                  data-testid="booking-widget-phone"
                  type="tel"
                  required
                  placeholder="070-123 45 67"
                  value={form.phone}
                  onChange={update("phone")}
                  className="swed-input"
                />
              </div>

              <div>
                <label className="block font-display font-bold text-[11px] uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  E-post <span className="text-[#4B5563] font-normal normal-case tracking-normal">(valfritt)</span>
                </label>
                <input
                  data-testid="booking-widget-email"
                  type="email"
                  placeholder="din@epost.se"
                  value={form.email}
                  onChange={update("email")}
                  className="swed-input"
                />
              </div>

              <div>
                <label className="block font-display font-bold text-[11px] uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  Tjänst
                </label>
                <select
                  data-testid="booking-widget-service"
                  value={form.service}
                  onChange={update("service")}
                  className="swed-input"
                >
                  <option value="">Välj tjänst (valfritt)</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>
                      {s.title}
                    </option>
                  ))}
                  <option value="Annat">Annat</option>
                </select>
              </div>

              <div>
                <label className="block font-display font-bold text-[11px] uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                  Meddelande
                </label>
                <textarea
                  data-testid="booking-widget-message"
                  rows={3}
                  placeholder="Berätta kort om ditt projekt (valfritt)"
                  value={form.message}
                  onChange={update("message")}
                  className="swed-input resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="booking-widget-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#D92525] hover:bg-[#0B132B] text-white font-display font-bold text-sm tracking-[0.18em] uppercase px-6 py-4 transition-colors disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Skickar…
                  </>
                ) : (
                  <>
                    <Send size={16} /> Skicka förfrågan
                  </>
                )}
              </button>

              <div className="text-center pt-2 border-t border-black/10">
                <div className="font-body text-xs text-black/60 mb-2">Eller ring oss direkt</div>
                <a
                  href={SITE.phoneHref}
                  data-testid="booking-widget-call"
                  className="inline-flex items-center gap-2 font-display font-bold text-sm tracking-tight text-[#0B132B] hover:text-[#D92525] transition-colors"
                >
                  <Phone size={14} strokeWidth={2.6} /> {SITE.phone}
                </a>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingWidget;
